import { useState } from "react";
import { toast } from "sonner";
import { Send, Mail, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const SUBJECTS = [
    { value: "billing", label: "Facturation / abonnement" },
    { value: "technical", label: "Bug / problème technique" },
    { value: "feature", label: "Suggestion de fonctionnalité" },
    { value: "data", label: "Mes données (RGPD)" },
    { value: "other", label: "Autre" },
];

export default function HelpPage() {
    const { user } = useAuth();
    const [form, setForm] = useState({
        subject: "technical",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.message.trim().length < 10) {
            toast.error("Votre message est trop court (10 caractères minimum)");
            return;
        }
        setSending(true);
        try {
            await api.post("/support/contact", {
                subject: form.subject,
                message: form.message,
            });
            setSent(true);
            setForm({ subject: "technical", message: "" });
            toast.success("Message envoyé. Nous revenons vers vous dans les plus brefs délais.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'envoi");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="space-y-8">
            <header>
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Centre d&apos;aide</h2>
                <p className="mt-1 text-sm text-[#86868B]">
                    Une question, un bug, une suggestion ? L&apos;équipe vous répond dans les plus brefs délais.
                </p>
            </header>

            {/* Quick links */}
            <section className="grid sm:grid-cols-3 gap-3">
                <FAQCard
                    title="Comment ça marche"
                    href="/comment-ca-marche"
                    body="Le détail technique : import bancaire, Gmail, matching, OCR."
                />
                <FAQCard
                    title="Tarifs et limites"
                    href="/tarifs"
                    body="Comparez les plans Free / Pro / Business."
                />
                <FAQCard
                    title="Honnêteté technique"
                    href="/honnetete"
                    body="Ce qu'on automatise vraiment et ce qu'on ne peut pas."
                />
            </section>

            {/* Contact form */}
            <section className="tk-card p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1D1D1F]">Contactez l&apos;équipe</h3>
                        <p className="text-sm text-[#86868B]">
                            Réponse depuis{" "}
                            <span className="font-mono text-[#1D1D1F]">support@ticketify.fr</span>
                        </p>
                    </div>
                </div>

                {sent ? (
                    <div className="text-center py-10" data-testid="contact-success">
                        <CheckCircle2 className="w-14 h-14 text-[#34C759] mx-auto mb-4" />
                        <h4 className="text-xl font-semibold text-[#1D1D1F]">Message bien reçu</h4>
                        <p className="mt-2 text-sm text-[#86868B]">
                            Nous vous répondrons à <span className="font-medium text-[#1D1D1F]">{user?.email}</span> dans les plus brefs délais.
                        </p>
                        <button
                            onClick={() => setSent(false)}
                            className="tk-btn tk-btn-secondary mt-6"
                            data-testid="contact-send-another"
                        >
                            Envoyer un autre message
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2 flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" /> Votre e-mail (pour la réponse)
                            </label>
                            <input
                                type="email"
                                value={user?.email || ""}
                                readOnly
                                className="tk-input opacity-70 cursor-not-allowed"
                                data-testid="contact-email"
                            />
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2 block">
                                Sujet
                            </label>
                            <select
                                value={form.subject}
                                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                className="tk-input"
                                data-testid="contact-subject"
                            >
                                {SUBJECTS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-[#86868B] mb-2 block">
                                Message
                            </label>
                            <textarea
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Décrivez précisément votre problème ou question…"
                                rows={6}
                                className="tk-input resize-none"
                                data-testid="contact-message"
                                required
                            />
                            <p className="mt-2 text-xs text-[#86868B]">
                                Minimum 10 caractères. Plus c&apos;est précis, plus on est rapide.
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={sending}
                                className="tk-btn tk-btn-primary"
                                data-testid="contact-submit"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" /> Envoyer
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </section>

            <p className="text-xs text-center text-[#86868B]">
                Vos messages sont stockés de manière sécurisée et utilisés uniquement pour vous répondre.
            </p>
        </div>
    );
}

function FAQCard({ title, body, href }) {
    return (
        <a
            href={href}
            className="tk-card p-5 hover:-translate-y-0.5 transition-all block"
            data-testid={`help-quick-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <p className="text-sm font-semibold text-[#1D1D1F]">{title}</p>
            <p className="mt-2 text-xs text-[#86868B] leading-relaxed">{body}</p>
        </a>
    );
}
